// Collapsed-state für die Projekt-Sektionen in der Sidebar (Favoriten /
// Meine Projekte / Für mich freigegeben / Organisation) + den übergeordneten
// "Projekte"-Block. Persistiert pro Sektion in localStorage, gespiegelt in ein
// Signal damit das UI ohne Prop-Drilling re-rendert. Folgt dem Muster von
// lib/sidebarState.ts.

import { signal } from "@preact/signals";

export type ProjectSectionId =
  | "root" // der "Projekte"-Parent
  | "favorites"
  | "mine"
  | "shared"
  | "org"
  | "listsRoot" // der "Listen"-Parent
  | "listsShared"; // die "Geteilt"-Gruppe innerhalb von Listen

const STORAGE_PREFIX = "juuplan:proj-section:";

// Defaults: alles offen außer "Organisation" (laut Design eingeklappt) und der
// Root-Block ist offen.
const DEFAULT_COLLAPSED: Record<ProjectSectionId, boolean> = {
  root: false,
  favorites: false,
  mine: false,
  shared: false,
  org: true,
  listsRoot: false,
  listsShared: false,
};

function read(id: ProjectSectionId): boolean {
  if (typeof localStorage === "undefined") return DEFAULT_COLLAPSED[id];
  const raw = localStorage.getItem(STORAGE_PREFIX + id);
  if (raw === "1") return true;
  if (raw === "0") return false;
  return DEFAULT_COLLAPSED[id];
}

// Ein Signal das alle Sektions-Zustände hält. Wird beim ersten Import aus
// localStorage hydriert.
const sectionsSignal = signal<Record<ProjectSectionId, boolean>>({
  root: read("root"),
  favorites: read("favorites"),
  mine: read("mine"),
  shared: read("shared"),
  org: read("org"),
  listsRoot: read("listsRoot"),
  listsShared: read("listsShared"),
});

export function useProjectSections(): Record<ProjectSectionId, boolean> {
  return sectionsSignal.value;
}

export function toggleProjectSection(id: ProjectSectionId): void {
  const next = { ...sectionsSignal.value, [id]: !sectionsSignal.value[id] };
  sectionsSignal.value = next;
  if (typeof localStorage !== "undefined") {
    localStorage.setItem(STORAGE_PREFIX + id, next[id] ? "1" : "0");
  }
}
